import { useCacheHelpers } from '../apollo/cache-helpers'
import {
  BuildingFragment,
  BuildingFragmentDoc,
  CreateEmissionInput,
  EmissionFragment,
  EmissionFragmentDoc,
  UpdateEmissionInput,
  useCreateEmissionMutation,
  useDeleteEmissionMutation,
  useUpdateEmissionMutation,
} from '../generated/graphql'

type EmissionMutations = {
  isCreateLoading: boolean
  isUpdateLoading: boolean
  isDeleteLoading: boolean

  createEmission: (
    input: CreateEmissionInput,
    buildingId: string,
  ) => Promise<EmissionFragment>

  updateEmission: (
    id: string,
    input: UpdateEmissionInput,
  ) => Promise<EmissionFragment>

  deleteEmission: (emissionId: string, buildingId: string) => Promise<boolean>
}

export function useEmissions(): EmissionMutations {
  const [createEmission, { loading: isCreateLoading }] =
    useCreateEmissionMutation()
  const [updateEmission, { loading: isUpdateLoading }] =
    useUpdateEmissionMutation()
  const [deleteEmission, { loading: isDeleteLoading }] =
    useDeleteEmissionMutation()
  const { addItemToList, getFragment, removeItemFromList } = useCacheHelpers()

  return {
    isCreateLoading,
    isUpdateLoading,
    isDeleteLoading,

    async createEmission(input: CreateEmissionInput, buildingId: string) {
      const result = await createEmission({
        variables: {
          buildingId,
          input,
        },

        update: (_, mutationResult) => {
          const emission = mutationResult.data?.createEmission
          if (!emission) {
            return
          }
          const building = getFragment<BuildingFragment>(
            buildingId,
            BuildingFragmentDoc,
          )

          addItemToList(EmissionFragmentDoc, emission, building, 'emissions')
        },
      })

      if (!result.data) {
        throw new Error('Emission creation failed')
      }

      return result.data.createEmission
    },

    async updateEmission(id: string, input: UpdateEmissionInput) {
      const result = await updateEmission({
        variables: { id, input },
      })

      if (!result.data) {
        throw new Error('Emission update failed')
      }

      return result.data.updateEmission
    },

    async deleteEmission(emissionId: string, buildingId: string) {
      const result = await deleteEmission({
        variables: { id: emissionId },

        update: () => {
          const building = getFragment<BuildingFragment>(
            buildingId,
            BuildingFragmentDoc,
          )

          removeItemFromList(
            EmissionFragmentDoc,
            emissionId,
            building,
            'emissions',
          )
        },

        optimisticResponse: {
          deleteEmission: true,
        },
      })

      if (!result.data) {
        throw new Error('Emission deletion failed')
      }

      return result.data.deleteEmission
    },
  }
}
